import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  Contractor,
  PagedResultDto,
  ContractorAvailabilityException,
  CreateContractorAvailabilityException,
  UpdateContractorAvailabilityException,
  AvailabilityExceptionParams,
  ContractorAvailabilityDto,
  ContractorScheduleDto,
  ContractorDocument,
  DocumentListQueryParams
} from '@umss/shared/data-access';
import { HttpHelper, ScopeOfPracticeEnum } from '@umss/shared/utils';
import { ContractorSearchDto } from '../dtos';
import { FacilityContextService } from './facility-context.service';

@Injectable({ providedIn: 'root' })
export class ContractorService {
  private apiUrl = '/api/contractors';
  private readonly facilityContext = inject(FacilityContextService);
  private get currentFacilityId(): string {
    return this.facilityContext.facilityId();
  }

  constructor(private http: HttpClient) {}

  /**
   * Search contractors with full filtering, sorting, and pagination support.
   * This is the primary method for the contractors list/grid view.
   * @param searchDto The search and filter parameters.
   * @returns An Observable of PagedResultDto<Contractor>.
   */
  searchContractors(
    searchDto: ContractorSearchDto
  ): Observable<PagedResultDto<Contractor>> {
    const mergedDto: ContractorSearchDto = {
      ...searchDto,
      ...(this.currentFacilityId
        ? {
            facilityId: this.currentFacilityId,
            facilityIdForDistance: this.currentFacilityId
          }
        : {})
    };
    const params = HttpHelper.toHttpParams(mergedDto);
    return this.http.get<PagedResultDto<Contractor>>(this.apiUrl, { params });
  }

  /**
   * Fetch available contractors based on various filters.
   * @param payload The filter parameters.
   * @returns An Observable array of ContractorAvailability.
   */
  getAvailableContractors(payload: {
    scope: ScopeOfPracticeEnum;
    latitude?: number;
    longitude?: number;
    radiusMiles?: number;
  }): Observable<Contractor[]> {
    const params = HttpHelper.toHttpParams(payload);
    return this.http.get<Contractor[]>(`${this.apiUrl}/available`, { params });
  }

  /**
   * Get a contractor's profile details.
   * @param contractorId The ID of the contractor.
   * @returns An Observable of the Contractor.
   */
  getContractor(contractorId: string): Observable<Contractor> {
    return this.http.get<Contractor>(`${this.apiUrl}/${contractorId}`);
  }

  /**
   * Get a contractor's availability schedule.
   * Returns recurring weekly availability with day of week and time ranges.
   * @param contractorId The ID of the contractor.
   * @returns An Observable array of ContractorAvailability.
   */
  getContractorAvailability(
    contractorId: string
  ): Observable<ContractorAvailabilityDto[]> {
    return this.http.get<ContractorAvailabilityDto[]>(
      `${this.apiUrl}/${contractorId}/availability`
    );
  }

  /**
   * Get a contractor's availability exceptions for a date range.
   * @param contractorId The ID of the contractor.
   * @param params Start and end date for filtering exceptions.
   * @returns An Observable array of ContractorAvailabilityException.
   */
  getContractorAvailabilityExceptions(
    contractorId: string,
    params?: AvailabilityExceptionParams
  ): Observable<ContractorAvailabilityException[]> {
    let httpParams = new HttpParams();

    if (params)
      httpParams = httpParams
        .append('startDate', params.startDate)
        .append('endDate', params.endDate);

    return this.http.get<ContractorAvailabilityException[]>(
      `${this.apiUrl}/${contractorId}/availability-exceptions`,
      { params: httpParams }
    );
  }

  /**
   * Create an availability exception for a contractor.
   * @param contractorId The ID of the contractor.
   * @param exception The exception data.
   * @returns An Observable of the created ContractorAvailabilityException.
   */
  createContractorAvailabilityException(
    contractorId: string,
    exception: CreateContractorAvailabilityException
  ): Observable<ContractorAvailabilityException> {
    return this.http.post<ContractorAvailabilityException>(
      `${this.apiUrl}/${contractorId}/availability-exceptions`,
      exception
    );
  }

  /**
   * Update an availability exception for a contractor.
   * @param contractorId The ID of the contractor.
   * @param exceptionId The ID of the exception.
   * @param exception The partial update data.
   * @returns An Observable of the updated ContractorAvailabilityException.
   */
  updateContractorAvailabilityException(
    contractorId: string,
    exceptionId: number,
    exception: UpdateContractorAvailabilityException
  ): Observable<ContractorAvailabilityException> {
    return this.http.put<ContractorAvailabilityException>(
      `${this.apiUrl}/${contractorId}/availability-exceptions/${exceptionId}`,
      exception
    );
  }

  /**
   * Delete an availability exception for a contractor.
   * @param contractorId The ID of the contractor.
   * @param exceptionId The ID of the exception.
   * @returns An Observable that completes when the operation is done.
   */
  deleteContractorAvailabilityException(
    contractorId: string,
    exceptionId: number
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${contractorId}/availability-exceptions/${exceptionId}`
    );
  }

  /**
   * Get a contractor's combined schedule (weekly availability + exceptions).
   * @param contractorId The ID of the contractor.
   * @param params Start and end date for filtering exceptions.
   * @returns An Observable of ContractorScheduleDto.
   */
  getContractorSchedule(
    contractorId: string,
    params: AvailabilityExceptionParams
  ): Observable<ContractorScheduleDto> {
    const httpParams = new HttpParams()
      .set('startDate', params.startDate)
      .set('endDate', params.endDate);
    return this.http.get<ContractorScheduleDto>(
      `${this.apiUrl}/${contractorId}/schedule`,
      { params: httpParams }
    );
  }

  /**
   * Mark a contractor as favorite for a facility.
   * @param contractorId The contractor ID.
   * @param facilityId The facility ID.
   * @returns Observable that completes when the operation is done.
   */
  addToFavorites(
    contractorId: string,
    facilityId: string
  ): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(
      `${this.apiUrl}/${contractorId}/favorite`,
      null,
      { params: { facilityId } }
    );
  }

  /**
   * Remove a contractor from favorites for a facility.
   * @param contractorId The contractor ID.
   * @param facilityId The facility ID.
   * @returns Observable that completes when the operation is done.
   */
  removeFromFavorites(
    contractorId: string,
    facilityId: string
  ): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${contractorId}/favorite`, {
      params: { facilityId }
    });
  }

  /**
   * Rate a contractor.
   * @param contractorId The ID of the contractor.
   * @param ratingData The rating information.
   * @returns An Observable that completes when the operation is done.
   */
  rateContractor(
    contractorId: string,
    ratingData: { rating: number; notes: string; education: string }
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/${contractorId}/rate`,
      ratingData
    );
  }

  /**
   * Mark a contractor as 'Do Not Return'.
   * @param contractorId The ID of the contractor.
   * @param dnrData The reason and notes for the DNR status.
   * @returns An Observable that completes when the operation is done.
   */
  setDnr(
    contractorId: string,
    dnrData: { notes: string; education: string }
  ): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${contractorId}/dnr`, dnrData);
  }

  /**
   * Terminate a contractor.
   * @param contractorId The ID of the contractor to terminate.
   * @param payload The termination details (rehire eligibility).
   * @returns An Observable that completes when the operation is done.
   */
  terminate(
    contractorId: string,
    payload: { isRehireEligible: boolean }
  ): Observable<void> {
    return this.http.post<void>(
      `${this.apiUrl}/${contractorId}/terminate`,
      payload
    );
  }

  // Documents
  /**
   * Get a contractor's documents (certifications, licenses, etc.)
   * @param id - Contractor ID
   * @param params - Optional query parameters (documentTypes[], isVerified, includeExpired)
   * @returns Observable of ContractorDocument array
   */
  getDocuments(
    id: string,
    params?: DocumentListQueryParams
  ): Observable<ContractorDocument[]> {
    return this.http.get<ContractorDocument[]>(
      `${this.apiUrl}/${id}/documents`,
      { params: HttpHelper.toHttpParams(params) }
    );
  }

  getBackgroundCheckReport(backgroundCheckId: string): Observable<Blob> {
    return this.http.get(`/api/background-checks/${backgroundCheckId}/report`, {
      responseType: 'blob'
    });
  }
}
